import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from './authContext';
import authService from '../services/authService';
import Toast from '../components/molecules/Toast';

/**
 * The "Preview mode" switch, as in version3: "Demo data" shows every page with
 * sample data; "Fresh start" shows the account's own data, so a new account
 * sees its real empty screens.
 *
 * The choice is saved on the user (Users.demoMode, via PATCH /preview-mode)
 * and arrives with user-details, so it follows them to any browser. Switching
 * applies at once and saves in the background; a failed save switches back.
 */
const DemoModeContext = createContext({ demo: false, setDemo: () => {} });

/**
 * The switch is hidden for now (commented out in components/Layout). With no
 * way to turn it off, a saved "Demo data" would leave an account stuck on
 * sample data, so every page shows the account's own data until this is
 * true again. The saved choice is left as it is on the user.
 */
const DEMO_MODE_ENABLED = false;

export function DemoModeProvider({ children }) {
  const { user, isLoggedIn } = useContextHook(AuthContext, v => ({ user: v.user, isLoggedIn: v.isLoggedIn }));
  const saved = DEMO_MODE_ENABLED && Boolean((user?.data || user)?.demoMode);
  const [demo, setDemoState] = useState(saved);

  // Follow the saved value whenever user-details is (re)loaded.
  useEffect(() => {
    setDemoState(saved);
  }, [saved]);

  const setDemo = useCallback(
    async next => {
      if (next === demo) return;
      setDemoState(next);
      if (!isLoggedIn) return;
      try {
        await authService.setPreviewMode({ demoMode: next });
      } catch (error) {
        setDemoState(!next);
        Toast({ type: 'error', message: error?.message || 'Could not switch preview mode.' });
      }
    },
    [demo, isLoggedIn],
  );

  const value = useMemo(() => ({ demo: isLoggedIn ? demo : false, setDemo }), [demo, isLoggedIn, setDemo]);
  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export const useDemoMode = () => useContext(DemoModeContext);
